import {GameStateEnums} from '../../../../../shared/GameEnums.ts'
import {
    type CardPopupInformation,
    CardDataController, type SelectCardPopupUsingDevelopmentCardFormat, type SelectCardPopupForPendingDistributionFormat,
    type SelectCardPopupStealCardFromAnotherPlayerFormat, type SelectCardToDiscardFormat, type SelectCardsToGiveToAnotherPlayer,
    type SelectPlayerUsingDevelopmentCardFormat, type SelectPlayerAndResourceCardFormat,
    type SelectCardFormat,
} from '@colonist/game-card-types'
import {type SelectDiceFormat, type PopupInformation, type SelectPlayerRobberFormat, ModeSettingEnum, type DicePair, type SelectPlayerFormat} from '@colonist/game-base-types'
import {type UIGameManager} from '../UIGameManager.ts'
import {type UIGameMechanic} from '../UIGameEventController.ts'
import {Sound} from '../../Sound.ts'
import {UIGamePopupInformationView} from './UIGamePopupInformationView.ts'
import {ClientUserState} from '../../ClientUserState.ts'
import {ActionState, CardEnum, type PlayerColor, TurnState} from '@colonist/game-states'
import {robberSkinController} from '../UIGameRobberSkinController.ts'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import IconPirateShip from '@static/images/icon_pirate_ship.svg'
import {cardEnumsToCardState, CARD_IMAGES, CARD_TIP_TITLE, type UIGameActionBoxPickCardProps} from '@colonist/ui-game'
import {type TranslationObject, getNestedTranslation, type TranslationKeys} from '@colonist/i18n'
import {type ImageURL} from '@colonist/types'
import IconDiscardResourceCards from '@static/images/icon_discard_resource_cards.svg'
import IconDiscardProgressCards from '@static/images/icon_discard_progress_cards.svg'
import {type InventorActionBoxData} from '../ui_game_map/UIGameInventorHighlightDiceController.ts'

export class UIGamePopupController implements UIGameMechanic {
    protected readonly uiGameManager: UIGameManager
    private turnStateOnPopupInitialization: TurnState
    private allowableActionState: ActionState
    private previousTurnState: TurnState
    private shouldShowBeginnerPopups: boolean
    private nextActionBoxId = 1

    private getNextActionBoxId(): number {
        const id = this.nextActionBoxId
        this.nextActionBoxId += 1
        return id
    }

    constructor(uiGameManager: UIGameManager) {
        this.uiGameManager = uiGameManager
        this.uiGameManager.eventController.registerUIGameMechanic(this)
        this.turnStateOnPopupInitialization = TurnState.InitialPlacement
        this.previousTurnState = TurnState.InitialPlacement
        this.shouldShowBeginnerPopups = this.getShouldShowBeginnerPopups()

        this.uiGameManager.gameStore.subscribe((state, prevState) => {
            const hasPlayerCardStateChanged = state.gameState.playerStates !== prevState.gameState.playerStates
            if(hasPlayerCardStateChanged) this.updatePickCardActionBoxPlayerCardState()
        })
    }

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.ConfirmUseDevelopmentCard: this.showBaseActionBox(data); break
            case UIGameEventEnums.SelectDiceNumbersConfirmation: this.showInventorBaseActionBox(data); break
            case UIGameEventEnums.SelectCardsFromBankUsingDevelopmentCard: this.showCardSelectorWindowForDevelopmentCard(data); break
            case UIGameEventEnums.SelectCardsForPendingDistribution: this.showCardSelectorWindowForPendingDistributions(data); break
            case UIGameEventEnums.SelectCardToGiveToAnotherPlayer: this.showCardSelectorWindowForGivingCardToAnotherPlayer(data); break
            case UIGameEventEnums.SelectCardsToDiscard: this.showCardDiscardPopup(data); break
            case UIGameEventEnums.SelectPlayer: this.showSelectPlayer(data); break
            case UIGameEventEnums.StealDevelopmentCardFromPlayer: this.showCardSelectorWindowToStealDevelopmentCard(data); break
            case UIGameEventEnums.StealResourceCardFromPlayer: this.showCardSelectorWindowToStealResourceCard(data); break
            case UIGameEventEnums.SelectPlayerForDevelopmentCard: this.showSelectPlayerUsingDevelopmentCard(data); break
            case UIGameEventEnums.SelectPlayerAndCard: this.showSelectPlayerAndResourceCard(data); break
            case UIGameEventEnums.SelectDice: this.showSelectDice(data); break
            case UIGameEventEnums.GameStateUpdated: this.onGameStateUpdated(); break
            case UIGameEventEnums.ClosePopupUI: this.closeUI(); break
            case UIGameEventEnums.ShowTradeUI: this.closeUIOnShowTrade(); break
            case UIGameEventEnums.BeginnerModeStart: this.shouldShowBeginnerPopups = true; break
            default: break
        }
    }

    private showCardSelectorWindowForDevelopmentCard(developmentCardSelectFormat: SelectCardPopupUsingDevelopmentCardFormat) {
        this.playSoundForCardSelectorWindow()

        const cardData = CardDataController.getCardDataForCard(developmentCardSelectFormat.developmentCardUsed)
        if(cardData == undefined) return

        const popupInformation = UIGamePopupInformationView.getPopupInformationForCardData(cardData)

        this.showPickCardActionBox(popupInformation, developmentCardSelectFormat.selectCardFormat, {
            isSelectFromHand: false,
            colorTitleOnSelect: false,
            hideBadgeOfSelectedCards: developmentCardSelectFormat.selectCardFormat.amountOfCardsToSelect == 1 ? true : false,
            hideBadgeOfCardsToSelect: true,
            removeCardFromSelectionOnClick: false,
            showSelectedCardFooterInformation: false,
        })
    }

    private showCardSelectorWindowForPendingDistributions(pendingDistributionSelectFormat: SelectCardPopupForPendingDistributionFormat) {
        const popupInformation = UIGamePopupInformationView.getPopupInformationForPendingDistributions(pendingDistributionSelectFormat)
        if(popupInformation == undefined) return

        this.showPickCardActionBox(popupInformation, pendingDistributionSelectFormat.selectCardFormat, {
            isSelectFromHand: false,
            colorTitleOnSelect: false,
            hideBadgeOfSelectedCards: true,
            hideBadgeOfCardsToSelect: true,
            removeCardFromSelectionOnClick: false,
            showSelectedCardFooterInformation: false,
        })
    }

    private showCardSelectorWindowForGivingCardToAnotherPlayer(selectCardFormat: SelectCardsToGiveToAnotherPlayer) {
        const cardData = CardDataController.getCardDataForCard(selectCardFormat.developmentCardUsed)
        if(cardData == undefined) return

        const popupInformation = UIGamePopupInformationView.getPopupInformationForCardData(cardData)
        popupInformation.body = selectCardFormat.body

        const playerValidator = this.uiGameManager.gameController.getPlayerState(selectCardFormat.playerReceivingCard)
        this.showPickCardActionBox(popupInformation, selectCardFormat.selectCardFormat, {
            isSelectFromHand: true,
            colorTitleOnSelect: false,
            hideBadgeOfSelectedCards: false,
            hideBadgeOfCardsToSelect: false,
            removeCardFromSelectionOnClick: false,
            showSelectedCardFooterInformation: false,
            playerValidator: playerValidator,
        })
    }

    private static getImageForCardDiscardPopup(selectCardFormat: SelectCardToDiscardFormat): ImageURL {
        switch(selectCardFormat.selectCardFormat.allowableActionState) {
            case ActionState.SelectCardsToDiscard:
                return IconDiscardResourceCards
            case ActionState.SelectProgressCardsToDiscard:
                return IconDiscardProgressCards
            default:
                throw new Error('Unsupported select card format')
        }
    }

    private showCardDiscardPopup(discardCardFormat: SelectCardToDiscardFormat) {
        const popupInformation: PopupInformation = {
            imagePath: UIGamePopupController.getImageForCardDiscardPopup(discardCardFormat),
            title: discardCardFormat.title,
            body: discardCardFormat.body,
        }

        this.showPickCardActionBox(popupInformation, discardCardFormat.selectCardFormat, {
            isSelectFromHand: true,
            colorTitleOnSelect: true,
            hideBadgeOfSelectedCards: !discardCardFormat.selectCardFormat.showCardBadge,
            hideBadgeOfCardsToSelect: !discardCardFormat.selectCardFormat.showCardBadge,
            removeCardFromSelectionOnClick: false,
            showSelectedCardFooterInformation: false,
        }, true)
    }

    private showSelectPlayer(selectionFormat: SelectPlayerRobberFormat) {
        const popupInformation: PopupInformation = {
            imagePath: selectionFormat.isPirate ? IconPirateShip : robberSkinController.getRobberSkin(),
            title: {key: selectionFormat.isPirate ? 'strings:game.tips.pirate.title' : 'strings:game.tips.robber.title'},
            body: {key: 'strings:game.prompts.selectWhoToRobFrom'},
        }

        const {isPirate, ...selectPlayerFormat} = selectionFormat
        this.showPickPlayerActionBox(popupInformation, selectPlayerFormat)
    }

    private showCardSelectorWindowToStealDevelopmentCard(stealDevelopmentCardFormat: SelectCardPopupStealCardFromAnotherPlayerFormat) {
        const cardData = CardDataController.getCardDataForCard(stealDevelopmentCardFormat.developmentCardUsed)
        if(cardData == undefined) return

        const popupInformation = UIGamePopupInformationView.getPopupInformationForCardData(cardData)

        const playerValidator = this.uiGameManager.gameController.getPlayerState(stealDevelopmentCardFormat.victim)
        this.showPickCardActionBox(popupInformation, stealDevelopmentCardFormat.selectCardFormat, {
            playerValidator: playerValidator,
            isSelectFromHand: false,
            colorTitleOnSelect: false,
            hideBadgeOfSelectedCards: true,
            hideBadgeOfCardsToSelect: true,
            removeCardFromSelectionOnClick: false,
            showSelectedCardFooterInformation: true,
        })
    }

    private showCardSelectorWindowToStealResourceCard(stealResourceCardFromAnotherPlayer: SelectCardPopupStealCardFromAnotherPlayerFormat) {
        const cardData = CardDataController.getCardDataForCard(stealResourceCardFromAnotherPlayer.developmentCardUsed)
        if(cardData == undefined) return

        const popupInformation = UIGamePopupInformationView.getPopupInformationForCardData(cardData)

        this.showPickCardActionBox(popupInformation, stealResourceCardFromAnotherPlayer.selectCardFormat, {
            isSelectFromHand: false,
            colorTitleOnSelect: false,
            hideBadgeOfSelectedCards: false,
            hideBadgeOfCardsToSelect: false,
            removeCardFromSelectionOnClick: true,
            showSelectedCardFooterInformation: false,
            playerValidator: this.uiGameManager.gameController.getPlayerState(stealResourceCardFromAnotherPlayer.victim),
        })
    }

    private showSelectPlayerUsingDevelopmentCard(developmentPlayerSelectFormat: SelectPlayerUsingDevelopmentCardFormat) {
        const cardData = CardDataController.getCardDataForCard(developmentPlayerSelectFormat.developmentCardUsed)
        if(cardData == undefined) return

        const popupInformation = UIGamePopupInformationView.getPopupInformationForCardData(cardData)

        if(developmentPlayerSelectFormat.overrideText != undefined) popupInformation.body = developmentPlayerSelectFormat.overrideText

        this.showPickPlayerActionBox(popupInformation, developmentPlayerSelectFormat.selectPlayerFormat, developmentPlayerSelectFormat)
    }

    private showSelectPlayerAndResourceCard(selectionFormat: SelectPlayerAndResourceCardFormat) {
        const cardData = CardDataController.getCardDataForCard(selectionFormat.developmentCardUsed)
        if(cardData == undefined) return

        const popupInformation = UIGamePopupInformationView.getPopupInformationForCardData(cardData)

        const showPickCardActionBoxOnPlayerSelect = (player: PlayerColor) => {
            this.showPickCardActionBox(popupInformation, {...selectionFormat.selectCardFormat, cancelButtonActive: true}, {
                playerValidator: this.uiGameManager.gameController.getPlayerState(player),
                isSelectFromHand: true,
            }, false, () => {
                this.showPickPlayerActionBox(popupInformation, {...selectionFormat.selectPlayerFormat, cancelButtonActive: true}, undefined, showPickCardActionBoxOnPlayerSelect, () => this.showCommercialHarborCancelWarning(selectionFormat))
            })
        }

        this.showPickPlayerActionBox(popupInformation, {...selectionFormat.selectPlayerFormat, cancelButtonActive: true}, undefined, showPickCardActionBoxOnPlayerSelect, () => this.showCommercialHarborCancelWarning(selectionFormat))
    }

    private showSelectDice(selectDiceFormat: SelectDiceFormat) {
        const cardData = CardDataController.getCardDataForCard(selectDiceFormat.developmentCardUsed)
        if(cardData == undefined) return

        const popupInformation = UIGamePopupInformationView.getPopupInformationForCardData(cardData)
        this.showPickDiceActionBox(selectDiceFormat.cancelButtonActive, popupInformation, selectDiceFormat.allowableActionState)
    }

    private playSoundForCardSelectorWindow() {
        switch(this.uiGameManager.gameController.currentState.actionState) {
            case ActionState.SelectCardsForPendingDistributions: Sound.playSound(Sound.SoundType.YourTurn); break
            default: break
        }
    }

    private isWrongState(): boolean {
        const currentActionBoxData = this.uiGameManager.gameStore.getState().actionBox.actionBoxData
        if(!currentActionBoxData) return false

        const didTurnStateChange = this.uiGameManager.gameController.currentState.turnState != this.turnStateOnPopupInitialization
        if(didTurnStateChange) return true

        const didActionStateChange = this.uiGameManager.gameController.currentStateValidator.getPlayerActionState(this.uiGameManager.gameController.myColor) != this.allowableActionState
        if(didActionStateChange) return true

        return false
    }

    private closeUI() {
        const hasActivePopup = this.uiGameManager.gameStore.getState().actionBox.actionBoxData != undefined
        if(!hasActivePopup) return

        this.uiGameManager.gameStore.getState().actionBox.hideActionBox()
        this.uiGameManager.eventController.sendEvent(undefined, UIGameEventEnums.PopupActive)
    }

    private closeUIOnShowTrade() {
        const hasActivePopup = this.uiGameManager.gameStore.getState().actionBox.actionBoxData != undefined
        if(!hasActivePopup) return

        this.uiGameManager.socketGameSend.cancelAction()
        this.closeUI()
    }

    private onGameStateUpdated() {
        if(this.isWrongState()) this.closeUI()

        if(!this.uiGameManager.gameController.isMyTurn) return
        if(!this.shouldShowBeginnerPopups) return

        if(this.shouldShowRobberInfoPopup()) this.onPlayerRolledSeven()
        if(this.shouldShowInitialPlacementPopup()) this.onInitialPlacements()
        this.previousTurnState = this.uiGameManager.gameController.currentState.turnState
    }

    private getShouldShowBeginnerPopups(): boolean {
        if(this.uiGameManager.gameController.isPlayerSpectator) return false
        if(ClientUserState.userState?.hasZeroCompletedGames()) return true
        return false
    }

    private shouldShowRobberInfoPopup(): boolean {
        const isValidActionState = ActionState.PlaceRobberOrPirate == this.uiGameManager.gameController.currentState.actionState
        const isPreviousTurnStateDice = this.previousTurnState == TurnState.Dice
        this.previousTurnState = this.uiGameManager.gameController.currentState.turnState

        if(!isPreviousTurnStateDice) return false
        if(!this.uiGameManager.gameController.diceState.diceThrown) return false
        if(!isValidActionState) return false
        if(this.uiGameManager.gameController.diceState.dice1 + this.uiGameManager.gameController.diceState.dice2 != 7) return false
        return true
    }

    private onPlayerRolledSeven() {
        if(this.uiGameManager.gameController.isReplay) return

        const popupInformation = UIGamePopupInformationView.getPopupInformationForPlayerRolledSeven(this.uiGameManager)
        this.showBaseActionBox(popupInformation)
    }

    private shouldShowInitialPlacementPopup(): boolean {
        const validGameModes = [ModeSettingEnum.Classic4P, ModeSettingEnum.Classic56P, ModeSettingEnum.Classic78P]
        if(!validGameModes.includes(this.uiGameManager.gameController.gameSettings.modeSetting)) return false
        if(this.uiGameManager.gameController.currentState.turnState != TurnState.InitialPlacement) return false
        return true
    }

    private onInitialPlacements() {
        if(this.uiGameManager.gameController.isReplay) return

        const currentActionState = this.uiGameManager.gameController.currentState.actionState
        const validActionStates = [ActionState.InitialPlacementRoadPlacement, ActionState.InitialPlacementPlaceSettlement]
        if(!validActionStates.includes(currentActionState)) return

        const popupInformation = currentActionState == ActionState.InitialPlacementRoadPlacement ? UIGamePopupInformationView.getPopupInformationForInitialRoadPlacement(this.uiGameManager) : UIGamePopupInformationView.getPopupInformationForInitialSettlementPlacement(this.uiGameManager)

        this.showBaseActionBox(popupInformation)
    }

    private setPopupState(allowableActionState: ActionState) {
        this.allowableActionState = allowableActionState
        this.turnStateOnPopupInitialization = this.uiGameManager.gameController.currentState.turnState
    }

    private showInventorBaseActionBox(inventorPopupData: InventorActionBoxData) {
        this.showBaseActionBox(inventorPopupData.popupInformation, inventorPopupData.onCheckAction, inventorPopupData.onCancelAction)
    }

    private showBaseActionBox(popupInformation: CardPopupInformation | PopupInformation, onConfirmOverride?: () => void, onCloseOverride?: () => void): boolean {
        const isCardPopupInformation = 'cardEnum' in popupInformation
        const cardsWithoutCancelButton = [CardEnum.VictoryPoint, CardEnum.ProgressCardPoliticsConstitution, CardEnum.ProgressCardSciencePrinter]
        const showCancelButton = isCardPopupInformation ? cardsWithoutCancelButton.includes(popupInformation.cardEnum) == false : false

        const allowableActionState = isCardPopupInformation ? popupInformation.allowableActionState ?? ActionState.None : ActionState.None

        this.uiGameManager.gameStore.getState().actionBox.setActionBoxData({id: this.getNextActionBoxId(), type: 'base', props: {
            title: isCardPopupInformation ? CARD_TIP_TITLE[popupInformation.cardEnum] : popupInformation.title,
            body: isCardPopupInformation ? popupInformation.cardBody : popupInformation.body,
            imagePath: isCardPopupInformation ? CARD_IMAGES[popupInformation.cardEnum] : popupInformation.imagePath,
            onConfirm: () => {
                if(onConfirmOverride != undefined) {
                    onConfirmOverride()
                    return
                }

                if(isCardPopupInformation) this.uiGameManager.socketGameSend.clickedDevelopmentCard(popupInformation.cardEnum)
                this.closeUI()
            },
            ...(showCancelButton && {onClose: () => {
                if(onCloseOverride != undefined) {
                    onCloseOverride()
                    return
                }

                this.uiGameManager.socketGameSend.cancelAction()
                this.closeUI()
            }}),
        }})

        this.uiGameManager.eventController.sendEvent(true, UIGameEventEnums.PopupActive)
        this.setPopupState(allowableActionState)

        return true
    }

    private updatePickCardActionBoxPlayerCardState() {
        const currentActionBoxData = this.uiGameManager.gameStore.getState().actionBox.actionBoxData
        if(!currentActionBoxData) return

        const isShowingBaseActionBox = currentActionBoxData.type === 'pickCard'
        if(isShowingBaseActionBox) {
            this.uiGameManager.gameStore.getState().actionBox.setActionBoxData({
                ...currentActionBoxData,
                props: {
                    ...currentActionBoxData.props,
                    playerCardState: cardEnumsToCardState(this.uiGameManager.gameController.getAllCardsInHand(this.uiGameManager.gameController.myColor)),
                },
            })
        }
    }

    private showPickCardActionBox(popupInformation: PopupInformation,
                                  selectCardFormat: SelectCardFormat,
                                  {isSelectFromHand, colorTitleOnSelect, hideBadgeOfSelectedCards, hideBadgeOfCardsToSelect, removeCardFromSelectionOnClick, showSelectedCardFooterInformation, playerValidator}: Partial<UIGameActionBoxPickCardProps>,
                                       showDiscardCardFraction: boolean = false, onCancelOverride?: () => void) {
        let selectedCards: CardEnum[] = []

        this.uiGameManager.gameStore.getState().actionBox.setActionBoxData({id: this.getNextActionBoxId(), type: 'pickCard', props: {
            title: popupInformation.title,
            body: popupInformation.body,
            imagePath: popupInformation.imagePath,
            amountOfCardsToSelect: selectCardFormat.amountOfCardsToSelect,
            initialCardsToSelect: selectCardFormat.validCardsToSelect,
            playerCardState: cardEnumsToCardState(this.uiGameManager.gameController.getAllCardsInHand(this.uiGameManager.gameController.myColor)),
            isSelectFromHand: isSelectFromHand ?? false,
            colorTitleOnSelect: colorTitleOnSelect ?? false,
            hideBadgeOfSelectedCards: hideBadgeOfSelectedCards ?? false,
            hideBadgeOfCardsToSelect: hideBadgeOfCardsToSelect ?? false,
            removeCardFromSelectionOnClick: removeCardFromSelectionOnClick ?? false,
            showSelectedCardFooterInformation: showSelectedCardFooterInformation ?? false,
            ...(playerValidator != undefined && {playerValidator: playerValidator}),
            onCardSelect: (newSelectedCards: CardEnum[]) => {
                selectedCards = newSelectedCards
                this.uiGameManager.socketGameSend.selectCardsState(newSelectedCards)
            },
            onConfirm: () => {
                if(selectedCards.length !== selectCardFormat.amountOfCardsToSelect) return
                this.uiGameManager.socketGameSend.selectCards(selectedCards)
                this.closeUI()
            },
            ...(selectCardFormat.cancelButtonActive && {
                onClose: () => {
                    if(onCancelOverride) {
                        onCancelOverride()
                        return
                    }

                    this.uiGameManager.socketGameSend.cancelAction()
                    this.closeUI()
                },
            }),
            ...(showDiscardCardFraction && {
                createTextOnSelect: (title: TranslationObject, selectedCount: number, totalRequired: number) => {
                    const isSaboteurDiscard = popupInformation.body.key === 'strings:game.prompts.saboteurDiscardCards'
                    const bodyOptions = isSaboteurDiscard
                        ? {...popupInformation.body.options, count: totalRequired}
                        : {...popupInformation.body.options, amountToDiscard: totalRequired}
                    return this.createCardFractionText(title, selectedCount, totalRequired, popupInformation.body.key, bodyOptions)
                },
            }),
        }})

        this.uiGameManager.eventController.sendEvent(true, UIGameEventEnums.PopupActive)
        this.setPopupState(selectCardFormat.allowableActionState)
    }

    private showPickPlayerActionBox(popupInformation: PopupInformation, selectPlayerFormat: SelectPlayerFormat, developmentPlayerSelectFormat?: SelectPlayerUsingDevelopmentCardFormat, onConfirmOverride?: (playerColor: PlayerColor) => void, onCancelOverride?: () => void) {
        let selectedPlayer: PlayerColor | undefined

        this.uiGameManager.gameStore.getState().actionBox.setActionBoxData({id: this.getNextActionBoxId(), type: 'pickPlayer', props: {
            title: popupInformation.title,
            body: popupInformation.body,
            imagePath: popupInformation.imagePath,
            playerValidators: selectPlayerFormat.playersToSelect.map((playerColor: PlayerColor) => this.uiGameManager.gameController.getPlayerState(playerColor)),
            onPlayerSelect: (player: PlayerColor) => {
                selectedPlayer = player
            },
            onConfirm: () => {
                if(selectedPlayer == undefined) return

                const shouldContinueDevelopmentCard = this.uiGameManager.gameController.currentState.actionState == ActionState.None && developmentPlayerSelectFormat?.developmentCardUsed != undefined
                if(shouldContinueDevelopmentCard) this.uiGameManager.socketGameSend.clickedDevelopmentCard(developmentPlayerSelectFormat.developmentCardUsed)

                this.uiGameManager.socketGameSend.selectPlayer(selectedPlayer)
                this.closeUI()

                if(onConfirmOverride != undefined) onConfirmOverride(selectedPlayer)
            },
            ...(selectPlayerFormat.cancelButtonActive && {
                onClose: () => {
                    if(onCancelOverride) {
                        onCancelOverride()
                    } else {
                        this.uiGameManager.socketGameSend.cancelAction()
                        this.closeUI()
                    }
                },
            }),
        }})

        this.uiGameManager.eventController.sendEvent(true, UIGameEventEnums.PopupActive)
        this.setPopupState(selectPlayerFormat.allowableActionState)
    }

    private showPickDiceActionBox(cancelButtonActive: boolean, popupInformation: PopupInformation, allowableActionState: ActionState) {
        let selectedDicePair: DicePair | undefined

        this.uiGameManager.gameStore.getState().actionBox.setActionBoxData({id: this.getNextActionBoxId(), type: 'pickDice', props: {
            title: popupInformation.title,
            body: popupInformation.body,
            imagePath: popupInformation.imagePath,
            onConfirm: () => {
                if(selectedDicePair == undefined) return
                this.uiGameManager.socketGameSend.confirmDiceSelection(selectedDicePair)
                this.closeUI()
            },
            ...(cancelButtonActive && {
                onClose: () => {
                    this.uiGameManager.socketGameSend.cancelAction()
                    this.closeUI()
                },
            }),
            onDiceSelected: (dicePair: DicePair) => {
                selectedDicePair = dicePair
            },
        }})

        this.uiGameManager.eventController.sendEvent(true, UIGameEventEnums.PopupActive)
        this.setPopupState(allowableActionState)
    }

    private readonly createCardFractionText = (title: TranslationObject, selectedCount: number, totalRequired: number, bodyKey: TranslationKeys, bodyOptions: Record<string, any>) => {
        const discardCardsFraction: TranslationObject = {
            key: 'strings:game.prompts.discardCardDenominator',
            options: {
                selectedCardsAmount: selectedCount,
                amountToDiscard: totalRequired,
            },
        }

        const discardCardsTitleAndFraction: TranslationObject = getNestedTranslation({
            key: 'strings:colonistCommon.utils.#append',
        }, {value1: title, value2: discardCardsFraction})

        const discardCardBody: TranslationObject = {
            key: bodyKey,
            options: bodyOptions,
        }

        return {
            title: discardCardsTitleAndFraction,
            body: discardCardBody,
        }
    }

    private showCommercialHarborCancelWarning(selectionFormat: SelectPlayerAndResourceCardFormat) {
        const cardData = CardDataController.getCardDataForCard(selectionFormat.developmentCardUsed)
        if(cardData == undefined) return

        const cancelPopupInformation: CardPopupInformation = {
            cardEnum: selectionFormat.developmentCardUsed,
            cardBody: {key: 'strings:game.prompts.endCommercialHarbor'},
            allowableActionState: selectionFormat.selectCardFormat.allowableActionState,
        }

        this.showBaseActionBox(cancelPopupInformation, () => {
            this.uiGameManager.socketGameSend.cancelAction()
            this.closeUI()
        }, () => {
            this.showSelectPlayerAndResourceCard(selectionFormat)
        })
    }
}
